/**
 * Login gate (ADR-010): a blocking overlay the app cannot proceed past without a session.
 *
 * Modelled on help.ts (a plain function returning handles, no class) but deliberately WITHOUT the
 * overlay's close affordances — no Escape, no backdrop click: this is a gate, not a dismissable
 * dialog. A real <form> with type=email/password so Enter submits and password managers engage.
 * In embed mode (?embed=1) the form is never rendered — a cross-site iframe would not receive the
 * Lax session cookie anyway (ADR-010 open question), so the card shows a link to the main app
 * instead of a credential prompt inside a foreign page.
 */

import { ApiError, getMe, login, type UserProfile } from './api.ts';
import { escapeHtml } from './dom.ts';
import { isEmbedded } from './embed.ts';

export interface LoginGate {
  /** Resolves with the profile once a session exists — immediately when the cookie is valid. */
  ensureLoggedIn: () => Promise<UserProfile>;
  /** Re-open the gate (session expiry / logout). Idempotent: five parallel 401s open ONE card. */
  open: () => void;
  teardown: () => void;
}

/**
 * `onReauthenticated` fires after a successful RE-login through open() — not for the initial
 * ensureLoggedIn(), whose caller simply continues after its await.
 */
export function createLoginGate(onReauthenticated: (profile: UserProfile) => void): LoginGate {
  let backdrop: HTMLDivElement | null = null;
  // The shared pending promise doubles as the "gate is open" flag; every caller awaits ONE login.
  let pending: Promise<UserProfile> | null = null;
  let settle: ((profile: UserProfile) => void) | null = null;

  const close = (): void => {
    backdrop?.remove();
    backdrop = null;
    document.body.classList.remove('locked');
  };

  const build = (): void => {
    backdrop = document.createElement('div');
    backdrop.className = 'login-backdrop';

    if (isEmbedded()) {
      // No credential form inside an iframe — link out to the main app (opens a full tab).
      const appUrl = new URL(window.location.href);
      appUrl.searchParams.delete('embed');
      backdrop.innerHTML = `
        <div class="login-card" role="dialog" aria-modal="true" aria-label="Přihlášení">
          <h2>Kroupy — detekce krupobití</h2>
          <p class="login-hint">Pro zobrazení dat se prosím přihlaste v
            <a href="${escapeHtml(appUrl.href)}" target="_blank" rel="noopener">hlavní aplikaci</a>.</p>
        </div>`;
      document.body.appendChild(backdrop);
      return;
    }

    backdrop.innerHTML = `
      <form class="login-card" role="dialog" aria-modal="true" aria-label="Přihlášení">
        <h2>Kroupy — přihlášení</h2>
        <label>E-mail
          <input type="email" name="email" autocomplete="username" required />
        </label>
        <label>Heslo
          <input type="password" name="password" autocomplete="current-password" required />
        </label>
        <p class="login-error" role="alert"></p>
        <button type="submit" class="login-submit">Přihlásit se</button>
      </form>`;

    const pick = <T extends Element>(selector: string): T => {
      const node = backdrop?.querySelector<T>(selector);
      if (!node) throw new Error(`login gate markup missing ${selector}`);
      return node;
    };
    const form = pick<HTMLFormElement>('form');
    const emailInput = pick<HTMLInputElement>('input[name="email"]');
    const passwordInput = pick<HTMLInputElement>('input[name="password"]');
    const errorEl = pick<HTMLParagraphElement>('.login-error');
    const submitBtn = pick<HTMLButtonElement>('.login-submit');

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      errorEl.innerHTML = '';
      submitBtn.disabled = true;
      login(emailInput.value.trim(), passwordInput.value)
        .then((profile) => {
          const resolve = settle;
          pending = null;
          settle = null;
          close();
          resolve?.(profile);
        })
        .catch((err: unknown) => {
          const text =
            err instanceof ApiError && err.status === 401
              ? 'Nesprávný e-mail nebo heslo.'
              : err instanceof Error
                ? err.message
                : 'Přihlášení selhalo.';
          // Error text can carry API/server strings — escaped like every other renderer (dom.ts).
          errorEl.innerHTML = escapeHtml(text);
          passwordInput.value = '';
          passwordInput.focus();
        })
        .finally(() => {
          submitBtn.disabled = false;
        });
    });

    document.body.appendChild(backdrop);
    emailInput.focus(); // the autofocus attribute is unreliable on dynamically inserted nodes
  };

  const show = (): Promise<UserProfile> => {
    if (pending === null) {
      pending = new Promise<UserProfile>((resolve) => {
        settle = resolve;
      });
      document.body.classList.add('locked');
      build();
    }
    return pending;
  };

  const ensureLoggedIn = async (): Promise<UserProfile> => {
    try {
      return await getMe();
    } catch (err: unknown) {
      // 401 = no session yet (the expected first visit); a network/API failure gates too — an
      // honest, visible state whose submit doubles as the retry.
      if (err instanceof ApiError) return show();
      throw err;
    }
  };

  const open = (): void => {
    if (pending !== null) return; // already gated — the five-parallel-401s case
    void show().then(onReauthenticated);
  };

  return {
    ensureLoggedIn,
    open,
    teardown: (): void => {
      close();
      pending = null;
      settle = null;
    },
  };
}
